import { PeriodLike, isTimingInMonth } from './seed-timing.util';

/**
 * Barres de période du calendrier, de la fiche et du guide (PC-11, K13).
 *
 * Quatre natures, toujours dites avec leur mot (R14) : le semis à l'abri
 * (`sheltered`, CJ-03), le semis en place, la plantation, la récolte. Le
 * calendrier ne traçait que le semis et la récolte : « Tomates : semis février
 * à avril » sans dire que c'est au chaud.
 */
export type PeriodKind = 'sow-sheltered' | 'sow' | 'plant' | 'harvest';

/** Ordre des pistes, de haut en bas : le geste du printemps d'abord. */
export const PERIOD_KINDS: PeriodKind[] = ['sow-sheltered', 'sow', 'plant', 'harvest'];

export interface ShelterablePeriod extends PeriodLike {
  sheltered?: boolean;
}

/** Une barre sur la frise, en mois décimaux de 0 (1er janvier) à 12 (31 décembre). */
export interface PeriodBar {
  kind: PeriodKind;
  start: number;
  end: number;
  /** Mois et jour de la période complète, pour la bulle (une période qui passe l'hiver a deux barres). */
  period: { startMonth: number; startDay: number; endMonth: number; endDay: number };
}

export function periodKind(period: ShelterablePeriod): PeriodKind | null {
  switch (period.type) {
    case 'SEEDLING':
      return period.sheltered ? 'sow-sheltered' : 'sow';
    case 'PLANTING':
      return 'plant';
    case 'HARVEST':
      return 'harvest';
    default:
      return null;
  }
}

/** Jours d'un mois en UTC, année non bissextile (la frise n'a pas d'année). */
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** En UTC, comme `seedTiming` : une fin au 30 avril à 23 h 59 UTC reste en avril. */
const monthDay = (date: Date | string): { month: number; day: number } => {
  const d = new Date(date);
  return { month: d.getUTCMonth() + 1, day: d.getUTCDate() };
};

/**
 * Les barres d'une liste de périodes. Une période qui passe l'hiver
 * (« octobre à mars ») se coupe en deux barres, fin d'année et début d'année.
 */
export function periodBars(periods: ShelterablePeriod[] | null | undefined): PeriodBar[] {
  const bars: PeriodBar[] = [];
  for (const period of periods ?? []) {
    const kind = periodKind(period);
    if (!kind) continue;
    const from = monthDay(period.startDate);
    const to = monthDay(period.endDate);
    const start = from.month - 1 + (from.day - 1) / DAYS_IN_MONTH[from.month - 1];
    const end = to.month - 1 + Math.min(to.day, DAYS_IN_MONTH[to.month - 1]) / DAYS_IN_MONTH[to.month - 1];
    const whole = { startMonth: from.month, startDay: from.day, endMonth: to.month, endDay: to.day };
    if (end > start) {
      bars.push({ kind, start, end, period: whole });
    } else {
      bars.push({ kind, start, end: 12, period: whole }, { kind, start: 0, end, period: whole });
    }
  }
  return bars;
}

/**
 * Le jour d'aujourd'hui sur la frise, à la même échelle que les barres (0 à 12) :
 * le 29 octobre tombe vers la fin d'octobre, pas au milieu du mois. Heure locale.
 */
export function dayPosition(date: Date): number {
  const month = date.getMonth();
  const days = DAYS_IN_MONTH[month];
  return month + (Math.min(date.getDate(), days) - 0.5) / days;
}

/** La période couvre-t-elle ce mois (1 à 12) ? */
export function isPeriodInMonth(period: PeriodLike, month: number): boolean {
  const from = monthDay(period.startDate);
  const to = monthDay(period.endDate);
  return isTimingInMonth({ kind: 'sow', startMonth: from.month, endMonth: to.month }, month);
}

/** Ce qui se fait ce mois-ci pour une culture : semer (à l'abri ou en place), planter, récolter. */
export interface MonthActions {
  sowSheltered: boolean;
  sow: boolean;
  plant: boolean;
  harvest: boolean;
}

export function monthActions(periods: ShelterablePeriod[] | null | undefined, month: number): MonthActions {
  const actions: MonthActions = { sowSheltered: false, sow: false, plant: false, harvest: false };
  for (const period of periods ?? []) {
    if (!isPeriodInMonth(period, month)) continue;
    switch (periodKind(period)) {
      case 'sow-sheltered':
        actions.sowSheltered = true;
        break;
      case 'sow':
        actions.sow = true;
        break;
      case 'plant':
        actions.plant = true;
        break;
      case 'harvest':
        actions.harvest = true;
        break;
    }
  }
  return actions;
}

/** Début, mi ou fin de mois : « mi-mai » se lit mieux qu'un « 15 mai » qui n'a rien de précis. */
export type MonthPart = 'early' | 'mid' | 'late';

export function monthPart(day: number, isEnd: boolean): MonthPart | null {
  if (day <= 10) return isEnd ? 'early' : null;
  if (day <= 20) return 'mid';
  return isEnd ? null : 'late';
}
